import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X, FileText, Shield, AlertTriangle, Scale } from 'lucide-react';
import { Button } from '@components/common/Button';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}

export const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose, onAccept, onDecline }) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="bg-ncaa-primary px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-white">
                          Terms and Conditions
                        </Dialog.Title>
                        <p className="text-sm text-blue-100">
                          DCEV Portal - Digital Civil Aviation Enrollment
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="max-h-96 overflow-y-auto px-6 py-6">
                  <div className="prose prose-sm max-w-none">
                    {/* Introduction */}
                    <section className="mb-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <Scale className="h-5 w-5 text-ncaa-primary" />
                        <h3 className="text-lg font-semibold text-gray-900 m-0">1. Introduction</h3>
                      </div>
                      <p className="text-gray-700">
                        These Terms and Conditions ("Terms") govern your use of the Digital Civil
                        Aviation Verification (DCEV) Portal operated by the Nigeria Civil Aviation
                        Authority (NCAA). By creating an account and using this service, you agree
                        to be bound by these Terms and all applicable laws and regulations.
                      </p>
                    </section>

                    {/* Purpose */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        2. Purpose of Service
                      </h3>
                      <p className="text-gray-700 mb-2">
                        The DCEV Portal is designed to facilitate:
                      </p>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        <li>Aviation license applications and renewals</li>
                        <li>Document submission and verification</li>
                        <li>Appointment scheduling at testing centers</li>
                        <li>Application status tracking</li>
                        <li>Communication between applicants and NCAA officials</li>
                      </ul>
                    </section>

                    {/* Eligibility */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Eligibility</h3>
                      <p className="text-gray-700 mb-2">To use this service, you must:</p>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        <li>Be at least 18 years of age</li>
                        <li>Hold a valid Nigerian National Identification Number (NIN)</li>
                        <li>Provide accurate and truthful information</li>
                        <li>Have the legal capacity to enter into this agreement</li>
                      </ul>
                    </section>

                    {/* Data Collection */}
                    <section className="mb-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <Shield className="h-5 w-5 text-green-600" />
                        <h3 className="text-lg font-semibold text-gray-900 m-0">
                          4. Data Collection and Privacy
                        </h3>
                      </div>
                      <p className="text-gray-700 mb-2">
                        We collect and process your personal information in accordance with:
                      </p>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        <li>Nigeria Data Protection Regulation (NDPR) 2019</li>
                        <li>NCAA data handling policies</li>
                        <li>International civil aviation security standards</li>
                      </ul>
                      <p className="text-gray-700 mt-2">
                        Your data will be used solely for aviation licensing purposes and may be
                        shared with relevant authorities as required by law.
                      </p>
                    </section>

                    {/* User Responsibilities */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        5. User Responsibilities
                      </h3>
                      <p className="text-gray-700 mb-2">As a user, you agree to:</p>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        <li>Provide accurate, complete, and current information</li>
                        <li>Maintain the confidentiality of your account credentials</li>
                        <li>Notify NCAA immediately of any unauthorized access</li>
                        <li>Use the service only for lawful purposes</li>
                        <li>Comply with all NCAA regulations and civil aviation laws</li>
                        <li>Submit only genuine documents and certifications</li>
                      </ul>
                    </section>

                    {/* Prohibited Activities */}
                    <section className="mb-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <h3 className="text-lg font-semibold text-gray-900 m-0">
                          6. Prohibited Activities
                        </h3>
                      </div>
                      <p className="text-gray-700 mb-2">You must not:</p>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        <li>Submit false or fraudulent information</li>
                        <li>Impersonate another person or entity</li>
                        <li>Attempt to circumvent security measures</li>
                        <li>Use the service for commercial purposes without authorization</li>
                        <li>Interfere with the proper functioning of the service</li>
                        <li>Share your account with unauthorized individuals</li>
                      </ul>
                    </section>

                    {/* Limitation of Liability */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        7. Limitation of Liability
                      </h3>
                      <p className="text-gray-700">
                        NCAA shall not be liable for any indirect, incidental, special,
                        consequential, or punitive damages arising from your use of the DCEV Portal.
                        Our liability is limited to the extent permitted by Nigerian law.
                      </p>
                    </section>

                    {/* Service Availability */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        8. Service Availability
                      </h3>
                      <p className="text-gray-700">
                        While we strive to maintain continuous service availability, NCAA does not
                        guarantee uninterrupted access. We reserve the right to suspend or modify
                        the service for maintenance, security, or regulatory compliance purposes.
                      </p>
                    </section>

                    {/* Account Termination */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        9. Account Termination
                      </h3>
                      <p className="text-gray-700">
                        NCAA reserves the right to suspend or terminate accounts that violate these
                        Terms or engage in activities that compromise the integrity of the aviation
                        licensing process. You may also close your account at any time by contacting
                        our support team.
                      </p>
                    </section>

                    {/* Governing Law */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        10. Governing Law
                      </h3>
                      <p className="text-gray-700">
                        These Terms are governed by the laws of the Federal Republic of Nigeria. Any
                        disputes arising from these Terms shall be subject to the exclusive
                        jurisdiction of Nigerian courts.
                      </p>
                    </section>

                    {/* Updates */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        11. Updates to Terms
                      </h3>
                      <p className="text-gray-700">
                        NCAA may update these Terms from time to time. We will notify you of
                        significant changes through the portal or via email. Continued use of the
                        service after such changes constitutes acceptance of the new Terms.
                      </p>
                    </section>

                    {/* Contact Information */}
                    <section className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        12. Contact Information
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700 mb-2">
                          <strong>Nigeria Civil Aviation Authority (NCAA)</strong>
                        </p>
                        <p className="text-gray-600 text-sm">
                          Email: support@dcev.ncaa.gov.ng
                          <br />
                          Phone: +234-9-461-5000
                          <br />
                          Address: Nnamdi Azikiwe International Airport, Abuja, Nigeria
                        </p>
                      </div>
                    </section>

                    {/* Effective Date */}
                    <section className="mb-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>Effective Date:</strong> January 1, 2024
                          <br />
                          <strong>Last Updated:</strong> September 29, 2024
                          <br />
                          <strong>Version:</strong> 1.0
                        </p>
                      </div>
                    </section>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="bg-gray-50 px-6 py-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Please read all terms carefully before accepting.
                    </div>
                    <div className="flex space-x-3">
                      <Button variant="outline" onClick={onDecline} className="px-6">
                        Decline
                      </Button>
                      <Button onClick={onAccept} className="px-6">
                        Accept Terms
                      </Button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
